import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { IAM_API, iam_api_requests } from "..";
import { useNotification } from "../../../components/common/GlobalNotification";
import { FilterGroup, NormalFilter } from "../../util/baseDto";
import { useExport } from "../../util/useExport";

const PATH = "/user";

const BASE_QUERY_KEY = IAM_API + PATH;

export interface ProfileUpdateRequest {
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  city: string | null;
  state: string | null;
  postalCode?: string | null;
  locality?: string | null;
}

export interface UserResponse {
  id: number;
  phoneNumber: number;
  countryCode: string;
  userType:
    | "CUSTOMER"
    | "SERVICE_PROVIDER"
    | "INTERNAL_USER"
    | "FIELD_OPERATOR";
  firstName: string;
  lastName: string;
  email: string;
  city: string;
  state: string;
  postalCode: string;
  locality?: string;
  requiresPasswordChange?: boolean;
  /** 1 = active, 2 = inactive, 3 = blocked (iam user status). */
  statusId?: number;
  createTimestamp?: number | null;
  lastLoginTime?: number | null;
}

const useUpdateProfileMutation = () => {
  const queryClient = useQueryClient();
  const { setSuccessNotification, setErrorNotification } = useNotification();

  return useMutation({
    mutationFn: async ({
      request,
    }: {
      request: ProfileUpdateRequest;
    }): Promise<UserResponse> =>
      iam_api_requests.post(PATH + "/update-profile", request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_QUERY_KEY] });
      setSuccessNotification("Profile updated Sucessfully");
    },
    onError: () => {
      setErrorNotification("Failed to update profile");
    },
  });
};

const useGetProfileDetails = () => {
  return useQuery({
    queryKey: [BASE_QUERY_KEY, "profile"],
    queryFn: async (): Promise<UserResponse> =>
      iam_api_requests.get(PATH + "/profile"),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

const useDeleteUserAccountMutation = () => {
  const queryClient = useQueryClient();
  const { setSuccessNotification, setErrorNotification } = useNotification();

  return useMutation({
    mutationFn: async ({
      userId,
      isSelfDelete,
    }: {
      userId: number;
      isSelfDelete?: boolean;
    }): Promise<string> => iam_api_requests.delete(`${PATH}/${userId}`),

    onSuccess: (_, variables) => {
      // logout ONLY if user deleted himself
      if (variables?.isSelfDelete) {
        localStorage.removeItem("sessionId");
      }

      queryClient.invalidateQueries({
        queryKey: [BASE_QUERY_KEY],
      });

      setSuccessNotification("Account deleted successfully");
    },

    onError: (error: any) => {
      setErrorNotification(
        error?.response?.data?.message || "Failed to delete account",
      );
    },
  });
};
const useCopyPhoneNumber = (id: number | null) => {
  const { setSuccessNotification, setErrorNotification } = useNotification();

  const { mutateAsync } = useMutation({
    mutationFn: async (): Promise<UserResponse> =>
      iam_api_requests.get(`${PATH}/${id}`),

    onSuccess: (userDetails) => {
      const phoneNumber = userDetails.phoneNumber.toString();
      const countryCode = userDetails.countryCode;
      const fullNumber = countryCode + phoneNumber;

      const isMobile =
        /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(
          navigator.userAgent,
        );

      if (isMobile) {
        window.location.href = `tel:${fullNumber}`;
        return;
      }

      navigator.clipboard.writeText(fullNumber);
      setSuccessNotification("Phone number copied to Clipboard!");
    },

    onError: () => {
      setErrorNotification("Failed to copy/open phone number");
    },
  });

  return {
    copyPhoneNumber: mutateAsync,
  };
};

const useOpenWhatsApp = (id: number | null) => {
  const { setErrorNotification } = useNotification();
  const { mutateAsync } = useMutation({
    mutationFn: async (_message?: string): Promise<UserResponse> =>
      iam_api_requests.get(`${PATH}/${id}`),
    onSuccess: (userDetails, message) => {
      const phoneNumber = userDetails.phoneNumber.toString();
      const countryCode = userDetails.countryCode;
      const text = message ? `?text=${encodeURIComponent(message)}` : "";
      const whatsappUrl = `https://wa.me/${countryCode}${phoneNumber}${text}`;
      window.open(whatsappUrl, "_blank");
    },
    onError: () => {
      setErrorNotification("Failed to open WhatsApp");
    },
  });

  return {
    openWhatsApp: mutateAsync,
  };
};
const useAdminUpdateUserMutation = () => {
  const queryClient = useQueryClient();
  const { setSuccessNotification, setErrorNotification } = useNotification();

  return useMutation({
    mutationFn: async ({
      userId,
      request,
    }: {
      userId: number;
      request: ProfileUpdateRequest;
    }): Promise<UserResponse> =>
      iam_api_requests.put(`${PATH}/${userId}/update`, request),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_QUERY_KEY] });
      setSuccessNotification("User updated successfully");
    },

    onError: (error: any) => {
      setErrorNotification(
        error?.response?.data?.message || "Failed to update user",
      );
    },
  });
};
const useGetUserById = (userId: number | null) => {
  return useQuery({
    queryKey: [BASE_QUERY_KEY, "by-id", userId],
    queryFn: async (): Promise<UserResponse> =>
      iam_api_requests.get(`${PATH}/${userId}`),
    enabled: !!userId,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

const useGetUserByPhoneNumber = (phoneNumber: string | null) => {
  return useQuery({
    queryKey: [BASE_QUERY_KEY, "by-phone", phoneNumber],
    queryFn: async (): Promise<UserResponse | null> => {
      const filter: FilterGroup = {
        operator: "AND",
        criteria: [NormalFilter("phoneNumber", "EQUALS", String(phoneNumber))],
        groups: [],
      };
      const response = await iam_api_requests.post(
        `${PATH}/search?page=0&size=1`,
        filter,
      );
      if (!response.data || response.data.length === 0) return null;
      return response.data[0];
    },
    enabled: !!phoneNumber && String(phoneNumber).trim().length > 0,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

const useSearchUserById = (userId: number | null) => {
  return useQuery({
    queryKey: [BASE_QUERY_KEY, "search-by-id", userId],
    queryFn: async (): Promise<UserResponse | null> => {
      const filter: FilterGroup = {
        operator: "AND",
        criteria: [NormalFilter("id", "EQUALS", userId)],
        groups: [],
      };
      const response = await iam_api_requests.post(
        `${PATH}/search?page=0&size=1`,
        filter,
      );
      if (!response.data || response.data.length === 0) return null;
      return response.data[0];
    },
    enabled: userId !== null,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

export const useExportUsers = () =>
  useExport(
    (filterGroup) => iam_api_requests.export(`${PATH}/export`, filterGroup),
    "users.xlsx",
    "Failed to export Users",
  );
export {
  BASE_QUERY_KEY as USER_DETAILS_QUERY_KEY,
  useUpdateProfileMutation,
  useGetProfileDetails,
  useCopyPhoneNumber,
  useOpenWhatsApp,
  useDeleteUserAccountMutation,
  useAdminUpdateUserMutation,
  useGetUserById,
  useGetUserByPhoneNumber,
  useSearchUserById,
};